import React, { type ComponentType, type ReactNode, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import CloseIcon from '@atlaskit/icon/core/migration/close--cross';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

const HEADER_HEIGHT = gridSize * 9; // 72px

export type Action = {
	icon: ComponentType<{
		label: string;
	}>;
	onClick: () => void;
	label: string;
};

export type Props = {
	title: string;
	onClose: (arg1: SyntheticEvent<HTMLElement>, arg2: UIAnalyticsEvent) => void;
};

export const GraphHeader = (props: Props) => {
	const { formatMessage } = useIntl();
	return (
		<Wrapper>
			<Title>{props.title}</Title>
			<Tooltip content={formatMessage(messages.closeLabel)} position="top">
				<Box xcss={closeButtonStyles}>
					<Button
						appearance="subtle-link"
						spacing="none"
						iconBefore={
							<CloseIcon
								spacing="spacious"
								color={token('color.icon')}
								label={formatMessage(messages.closeLabel)}
							/>
						}
						onClick={props.onClose}
						testId="servicedesk-insight-object-graph.ui.graph-header.button-close"
					/>
				</Box>
			</Tooltip>
		</Wrapper>
	);
};

const Wrapper = ({ children }: { children?: ReactNode }) => (
	<Flex alignItems="center" xcss={wrapperNewStyles}>
		{children}
	</Flex>
);

const Title = ({ children }: { children?: ReactNode }) => (
	<Box xcss={titleStyles}>
		<Heading size="medium" as="h1">
			{children}
		</Heading>
	</Box>
);

const closeButtonStyles = xcss({
	marginLeft: 'space.200',
});

const wrapperNewStyles = xcss({
	boxSizing: 'border-box',
	padding: 'space.300',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `2px solid ${token('color.border')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${HEADER_HEIGHT}px`,
});

const titleStyles = xcss({
	color: 'color.text',
	margin: 'space.0',
	flexGrow: '1',
	marginTop: 'space.050',
	paddingBottom: 'space.050',
	fontWeight: 'font.weight.regular',
});
